import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Overlay } from "src/types/osbuilder";
import { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { showErrorToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import {
    getTargetNameConflict,
    hasDuplicateOverlay,
    overlayValidationSchema,
    resourceTypeOptions,
} from "./AddOverlayDialog.validation";
import SharedUtils from "./ModulePanel.utils";

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(4), marginTop: theme.spacing(3) },
}));

type FormValues = {
    targetPackage: string;
    resourcePath: string;
    resourceName: string;
    resourceType: Overlay["resourceType"];
    value: string;
    targetName?: string;
};

type Props = {
    product: Product | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

export const AddOverlayDialog = (props: Props) => {
    const [updateProduct] = useUpdateProductMutation();
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);
    const validate = useValidationSchema(overlayValidationSchema);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = cannotManageProducts || !props.product;

    const onClose = (form: FormApi<FormValues>) => {
        form.restart();
        props.onToggle(false);
    };

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        if (props.product) {
            const overlays = props.product.customBuildConfig?.overlays;

            if (hasDuplicateOverlay(overlays, values)) {
                showErrorToast(t("modals:manageProductModules.addOverlay.toast.duplicate"));
                return;
            }

            const targetNameConflict = getTargetNameConflict(overlays, values);
            if (targetNameConflict) {
                showErrorToast(
                    targetNameConflict.targetName
                        ? t("modals:manageProductModules.addOverlay.toast.targetNameConflict", {
                              targetPackage: values.targetPackage.trim(),
                              targetName: targetNameConflict.targetName,
                          })
                        : t("modals:manageProductModules.addOverlay.toast.targetNameMissing", {
                              targetPackage: values.targetPackage.trim(),
                          }),
                );
                return;
            }

            const newOverlay: Overlay = {
                targetPackage: values.targetPackage.trim(),
                resourcePath: values.resourcePath.trim(),
                resourceName: values.resourceName.trim(),
                resourceType: values.resourceType,
                value: values.value.trim(),
                targetName: values.targetName?.trim() || null,
            };

            const newProduct = _.cloneDeep(props.product);
            if (newProduct.customBuildConfig) {
                newProduct.customBuildConfig.overlays = [...(overlays ?? []), newOverlay];
            }
            const saved = await SharedUtils.saveProduct(updateProduct, newProduct);
            if (!saved) {
                return;
            }
        }
        onClose(form);
    };

    const getTooltipTitle = () => {
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ resourceType: "string" }}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    maxWidth="md"
                    fullWidth
                    open={props.open}
                    onClose={() => onClose(form)}
                    title={t("modals:manageProductModules.addOverlay.title")}
                    description={t("modals:manageProductModules.addOverlay.description")}
                    endButton={
                        <Tooltip hide={!formDisabled} title={getTooltipTitle()}>
                            <PrimaryButton onClick={form.submit} disabled={submitting || formDisabled} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <TextField
                            name="targetPackage"
                            label={t("modals:manageProductModules.addOverlay.input.targetPackage.caption")}
                            placeholder={t("modals:manageProductModules.addOverlay.input.targetPackage.placeholder")}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <TextField
                            name="resourcePath"
                            label={t("modals:manageProductModules.addOverlay.input.resourcePath.caption")}
                            placeholder={t("modals:manageProductModules.addOverlay.input.resourcePath.placeholder")}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <TextField
                            name="resourceName"
                            label={t("modals:manageProductModules.addOverlay.input.resourceName.caption")}
                            placeholder={t("modals:manageProductModules.addOverlay.input.resourceName.placeholder")}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <ControlledSelectField
                            name="resourceType"
                            label={t("modals:manageProductModules.addOverlay.input.resourceType.caption")}
                            options={resourceTypeOptions}
                            fullWidth
                            disableSorting
                            disabled={formDisabled}
                        />
                        <TextField
                            name="value"
                            label={t("common:label.value")}
                            placeholder={t("modals:manageProductModules.addOverlay.input.value.placeholder")}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <TextField
                            name="targetName"
                            label={t("modals:manageProductModules.addOverlay.input.targetName.caption")}
                            placeholder={t("modals:manageProductModules.addOverlay.input.targetName.placeholder")}
                            fullWidth
                            disabled={formDisabled}
                        />
                    </form>
                </Modal>
            )}
        />
    );
};
